/**
 * Topture Predictive Search
 * ─────────────────────────
 * Replaces Empire's native live-search results with a Boost-style
 * two-column layout: Collections + Pages on the left, Products on the right.
 *
 * Uses Shopify's Predictive Search API (/search/suggest.json).
 *
 * Strategy: COOPERATE with Empire's animation system rather than fight it.
 * - We let Empire's event handlers run (no stopImmediatePropagation)
 * - We set the animation attributes Empire's CSS expects
 * - We use our own container (#ps-custom-results) + .ps-searching class
 *   to hide Empire's native results while showing ours
 * - On desktop: override Empire's height collapse (--open-height: 0px)
 * - On mobile: set live-search--takeover + data-animation-state on liveSearchRoot
 * - Empire's quick links (popular searches) remain visible when input is empty
 */

(function () {
  'use strict';

  // ════════════════════════════════════════════
  // Configuration
  // ════════════════════════════════════════════
  var CONFIG = {
    debounceMs: 300,
    minChars: 2,
    limits: {
      display: 6,       // How many products to SHOW in the flydown
      fetch: 10,        // How many to FETCH from API — extras compensate for filtering
      collections: 5,
      pages: 3
    },
    resourceTypes: 'product,collection,page',

    // ────────────────────────────────────────────
    // ALGOLIA SEARCH BACKEND
    // Search-only API key — safe to expose client-side.
    // ────────────────────────────────────────────
    algolia: {
      appId:        'RIQCXA3QR5',
      searchApiKey: '5ae46c2fd93bfa5370985fc87078b31a',
      indices: {
        products:    'shopify_products',
        collections: 'shopify_collections',
        pages:       'shopify_pages'
      }
    },

    // CDN URL rewrite: API returns old cdn.shopify.com URLs,
    // but the store serves images from topture.com/cdn/shop/
    cdnRewrite: {
      from: 'cdn.shopify.com/s/files/1/0542/4037/9061/',
      to: 'topture.com/cdn/shop/'
    },

    // ────────────────────────────────────────────
    // RESULT FILTERING & SORTING RULES
    // Edit these arrays to control what shows in search results.
    // All matching is case-insensitive.
    // ────────────────────────────────────────────

    // Products with these product types get pushed to the BOTTOM (still visible)
    // The API returns product type as the "type" field (not "product_type")
    deprioritizeProductTypes: [
      'Sauna Heater Accessories',
      'Sauna Accessories'
    ],

    // Products whose TITLE contains any of these words also get pushed to BOTTOM
    // (Additional safety net alongside product type matching)
    deprioritizeProductTitleContains: [
      'backrest',
      'bucket',
      'ladle',
      'thermometer',
      'hygrometer',
      'sand timer',
      'headrest',
      'chimney',
      'extension pipe',
      'brush',
      'cleaning'
    ],

    // Products whose title contains any of these words are HIDDEN entirely
    hideProductTitleContains: [
      'stones',
      'rocks'
    ],

    // Pages whose URL contains any of these strings are HIDDEN entirely
    hidePageUrlContains: [
      '/blogs/guides'
    ],

    // Pages whose Shopify page ID (Algolia objectID) matches any of these
    // are HIDDEN entirely. IDs are bare numeric strings from the admin URL.
    hidePageIds: [
      '76451119285',  '81316380853',  '95436570843',  '96958120155',
      '97195622619',  '101757059336', '102372835592', '119580033288',
      '143274639624', '145603461384', '152017207560', '152286134536',
      '152349212936', '156718399752', '156840755464', '158904779016',
      '161727873288', '163147710728', '163147809032', '163148759304',
      '163149676808', '163213541640', '163215966472', '163217375496',
      '163261022472', '163261415688', '163261579528', '163261645064',
      '163261710600', '163261776136', '163261808904', '163261907208'
    ],

    // Collections whose URL contains any of these strings are HIDDEN entirely
    // (empty = show all collections)
    hideCollectionUrlContains: []
  };

  // ════════════════════════════════════════════
  // Mobile detection
  // ════════════════════════════════════════════
  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  // ════════════════════════════════════════════
  // Inject CSS
  // ════════════════════════════════════════════
  function injectStyles() {
    if (document.getElementById('predictive-search-styles')) return;
    var style = document.createElement('style');
    style.id = 'predictive-search-styles';
    style.textContent = [
      /* ── Our custom results container ── */
      '#ps-custom-results { display: none; }',
      '#ps-custom-results.ps-visible { display: block !important; }',

      /* Screen-reader-only status line (WCAG 4.1.3). Must stay in the layout —
         display:none and visibility:hidden remove a live region from the
         accessibility tree entirely, so nothing would ever be announced. */
      '.ps-sr-status { position: absolute !important; width: 1px !important; height: 1px !important;'
        + ' padding: 0 !important; margin: -1px !important; overflow: hidden !important;'
        + ' clip: rect(0 0 0 0) !important; clip-path: inset(50%) !important; white-space: nowrap !important;'
        + ' border: 0 !important; }',

      /* Keyboard focus ring for arrow-key navigation through results. The
         theme's global :focus-visible baseline does not match programmatic
         .focus() from a keydown handler in every browser, so make it explicit. */
      '.ps-flydown__product:focus, .ps-flydown__link-item a:focus,'
        + ' .ps-flydown__view-all a:focus { outline: 3px solid #19733e; outline-offset: -3px; }',

      /* ── Hide Empire's native results when our search is active ── */
      '.search-flydown.ps-searching .search-flydown--placeholder { display: none !important; }',
      /* Empire native results are permanently replaced by #ps-custom-results.
         Hide them unconditionally — if we only hide them while .ps-searching
         is active, Empire still populates them in parallel and they become
         visible again when the user backspaces below minChars, showing
         Empire\'s live search on top of our quicklinks. */
      '.search-flydown .search-flydown--results { display: none !important; }',
      '.search-flydown.ps-searching .search-flydown--quicklinks { display: none !important; }',

      /* ── On mobile: hide Empire's placeholder + results in our overlay ── */
      /* But SHOW quicklinks when not actively searching (pre-typing state) */
      '@media (max-width: 749px) {',
      '  .search-flydown.ps-mobile-open .search-flydown--placeholder { display: none !important; }',
      '  .search-flydown.ps-mobile-open .search-flydown--results { display: none !important; }',
      '  .search-flydown.ps-mobile-open .search-flydown--quicklinks { display: block !important; opacity: 1 !important; padding: 20px 16px; }',
      '  .search-flydown.ps-mobile-open.ps-searching .search-flydown--quicklinks { display: none !important; }',
      '}',

      /* ── Quick links title styling ── */
      '.search-flydown--quicklinks-title { text-align: center; color: #999; }',

      /* ── Desktop: flydown matches search bar width ── */
      '@media (min-width: 750px) {',
      '  .search-flydown.ps-active { display: block !important; position: absolute !important; top: 100% !important; left: 0 !important; right: 0 !important; width: 100% !important; max-height: 70vh !important; overflow: visible !important; z-index: 99999 !important; background: #fff; box-shadow: 0 8px 32px rgba(0,0,0,0.12); border: 1px solid #e5e5e5; border-top: none; }',
      '  .ps-flydown { max-height: 70vh; overflow-y: auto; }',
      '  .ps-flydown__products { grid-template-columns: 1fr 1fr !important; gap: 8px 16px; }',
      '  .ps-flydown__product { flex-direction: row; }',
      '  .ps-flydown__product-image { flex: 0 0 72px; width: 72px; height: 72px; }',
      '  .live-search--focused { z-index: 99999 !important; }',
      '  .ps-mobile-header { display: none !important; }',
      '}',

      /* ── Two-column layout ── */
      '.ps-flydown { display: flex; flex-direction: row; width: 100%; max-height: 70vh; overflow-y: auto; padding: 0; }',
      '.ps-flydown--empty { display: none; }',

      /* ── Left column: Collections + Pages ── */
      '.ps-flydown__left { flex: 0 0 240px; padding: 20px; border-right: 1px solid #eee; overflow-y: auto; }',
      '.ps-flydown__section-title { font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #666; margin: 0 0 12px 0; }',
      '.ps-flydown__section-title:not(:first-child) { margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee; }',
      '.ps-flydown__link-list { list-style: none; margin: 0; padding: 0; }',
      '.ps-flydown__link-item { margin: 0 0 8px 0; }',
      '.ps-flydown__link-item a { color: #2a7d4f; font-size: 14px; text-decoration: none; line-height: 1.4; display: block; }',
      '.ps-flydown__link-item a:hover { text-decoration: underline; }',

      /* ── Right column: Products ── */
      '.ps-flydown__right { flex: 1; padding: 20px; overflow-y: auto; min-width: 0; }',
      '.ps-flydown__products { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 24px; }',
      '.ps-flydown__product { display: flex; gap: 12px; text-decoration: none; color: inherit; padding: 8px; border-radius: 4px; transition: background 0.15s; min-width: 0; }',
      '.ps-flydown__product:hover { background: #f8f8f8; }',
      '.ps-flydown__product-image { flex: 0 0 80px; width: 80px; height: 80px; overflow: hidden; border-radius: 4px; background: #f5f5f5; }',
      '.ps-flydown__product-image img { width: 100%; height: 100%; object-fit: contain; }',
      '.ps-flydown__product-info { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; overflow: hidden; }',
      '.ps-flydown__product-title { font-size: 14px; font-weight: 500; color: #333; line-height: 1.3; margin: 0 0 2px 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }',


      '.ps-flydown__product-price { font-size: 14px; font-weight: 600; color: #2a7d4f; margin: 0 0 4px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }',
      '.ps-flydown__product-price--compare { text-decoration: line-through; color: #999; font-weight: 400; margin-right: 6px; }',

      /* ── View all link ── */
      '.ps-flydown__view-all { display: flex; justify-content: center; padding: 16px 20px 8px; border-top: 1px solid #eee; margin-top: 16px; }',
      '.ps-flydown__view-all a { color: #2a7d4f; font-size: 14px; font-weight: 500; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }',
      '.ps-flydown__view-all a:hover { text-decoration: underline; }',
      '.ps-flydown__view-all-arrow { font-size: 16px; }',

      /* ── No results ── */
      '.ps-flydown__no-results { padding: 40px 20px; text-align: center; color: #666; font-size: 14px; }',

      /* ── Loading state ── */
      '.ps-flydown__loading { padding: 40px 20px; text-align: center; }',
      '.ps-flydown__spinner { display: inline-block; width: 24px; height: 24px; border: 2px solid #eee; border-top-color: #2a7d4f; border-radius: 50%; animation: ps-spin 0.6s linear infinite; }',
      '@keyframes ps-spin { to { transform: rotate(360deg); } }',

      /* ── Mobile: full-page search takeover ── */
      '@media (max-width: 749px) {',

      /* Full-screen flydown — activates on ps-active (search) OR ps-mobile-open (focus) */
      '  .search-flydown.ps-active, .search-flydown.ps-mobile-open { position: fixed !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; width: 100% !important; height: 100% !important; max-height: none !important; z-index: 99999 !important; background: #fff !important; display: block !important; overflow-y: auto !important; -webkit-overflow-scrolling: touch; }',

      /* Mobile search header bar */
      '  .ps-mobile-header { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-bottom: 1px solid #eee; position: sticky; top: 0; background: #fff; z-index: 1; }',
      '  .ps-mobile-header__back { flex: 0 0 auto; background: none; border: none; padding: 4px; cursor: pointer; color: #333; font-size: 20px; line-height: 1; }',
      '  .ps-mobile-header__input { flex: 1; border: 1px solid #ddd; border-radius: 24px; padding: 10px 16px; font-size: 16px; outline: none; -webkit-appearance: none; }',
      '  .ps-mobile-header__input:focus { border-color: #2a7d4f; }',
      '  .ps-mobile-header__clear { flex: 0 0 auto; background: none; border: none; padding: 4px 8px; cursor: pointer; color: #999; font-size: 18px; line-height: 1; }',

      /* Our results container */
      '  #ps-custom-results.ps-visible { display: block !important; width: 100%; }',

      /* Stack vertically, no max-height — let it scroll naturally */
      '  .ps-flydown { flex-direction: column; max-height: none; overflow: visible; padding: 0; }',

      /* Products first and full-width — the main content */
      '  .ps-flydown__right { order: -1; padding: 16px 16px 0; flex: none; }',
      '  .ps-flydown__products { grid-template-columns: 1fr 1fr; gap: 16px 12px; }',
      '  .ps-flydown__product { flex-direction: column; gap: 0; padding: 0; }',
      '  .ps-flydown__product-image { flex: none; width: 100%; height: auto; aspect-ratio: 1; border-radius: 8px; margin-bottom: 8px; }',
      '  .ps-flydown__product-title { font-size: 13px; line-height: 1.3; margin-bottom: 2px; }',


      '  .ps-flydown__product-price { font-size: 14px; }',
      '  .ps-flydown__view-all { padding: 16px; margin-top: 8px; }',

      /* Collections + Pages below the fold */
      '  .ps-flydown__left { flex: none; order: 1; border-right: none; border-top: 1px solid #eee; padding: 16px; }',

      /* Section titles centered on mobile */
      '  .ps-flydown__section-title { text-align: center; }',

      /* Hide chat widget & Google seller rating behind mobile overlay */
      '  body.ps-overlay-active #crisp-chatbox { display: none !important; }',
      '  body.ps-overlay-active #google-merchantwidget-iframe-wrapper { display: none !important; }',

      '}'
    ].join('\n');
    document.head.appendChild(style);
  }

  // ════════════════════════════════════════════
  // CDN URL rewrite
  // ════════════════════════════════════════════
  function rewriteImageUrl(url) {
    if (!url) return '';
    // Ensure protocol
    if (url.indexOf('//') === 0) {
      url = 'https:' + url;
    }
    // Rewrite cdn.shopify.com → topture.com/cdn/shop/
    url = url.replace(CONFIG.cdnRewrite.from, CONFIG.cdnRewrite.to);
    return url;
  }

  // ════════════════════════════════════════════
  // Algolia search backend
  // ════════════════════════════════════════════
  // Posts a multi-index query to Algolia's REST API, then maps the response
  // to the same shape the renderer expects (data.resources.results.{products,
  // collections, pages}) so the rest of the file doesn't care about the
  // backend swap.
  // ════════════════════════════════════════════
  var _abortController = null;

  function buildAlgoliaUrl() {
    return 'https://' + CONFIG.algolia.appId.toLowerCase() +
           '-dsn.algolia.net/1/indexes/*/queries';
  }

  // Algolia stores Shopify prices as decimal dollars (e.g. 1930 or 1930.99).
  // The existing renderer's formatMoney expects cents. Convert and pass
  // through the same variants[] shape so renderFlydown keeps working.
  function priceToCents(dollars) {
    if (dollars == null) return null;
    var n = Number(dollars);
    if (isNaN(n)) return null;
    return Math.round(n * 100);
  }

  function mapAlgoliaProduct(hit) {
    var minPrice = hit.variants_min_price != null ? hit.variants_min_price : hit.price;
    var minCompare = hit.variants_compare_at_price_min != null
      ? hit.variants_compare_at_price_min
      : hit.compare_at_price;
    return {
      title: hit.title,
      url: '/products/' + hit.handle,
      type: hit.product_type,
      image: hit.product_image || hit.image,
      featured_image: hit.product_image || hit.image,
      variants: [{
        price: priceToCents(minPrice),
        compare_at_price: minCompare != null ? priceToCents(minCompare) : null
      }]
    };
  }

  function mapAlgoliaCollection(hit) {
    return {
      title: hit.title,
      url: '/collections/' + hit.handle
    };
  }

  function mapAlgoliaPage(hit) {
    // Algolia indexes both pages and articles in shopify_pages depending on
    // the Shopify Algolia integration version; fall back gracefully.
    var url = hit.url || ('/pages/' + hit.handle);
    return {
      title: hit.title,
      url: url
    };
  }

  function fetchPredictiveSearch(query, callback) {
    if (_abortController) {
      try { _abortController.abort(); } catch (e) {}
    }
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    _abortController = controller;

    var q = encodeURIComponent(query);
    var body = {
      requests: [
        {
          indexName: CONFIG.algolia.indices.products,
          // distinct=true honors the index's attributeForDistinct setting,
          // collapsing variant-level records down to one hit per product.
          params: 'query=' + q + '&hitsPerPage=' + CONFIG.limits.fetch + '&distinct=true'
        },
        {
          indexName: CONFIG.algolia.indices.collections,
          params: 'query=' + q + '&hitsPerPage=' + CONFIG.limits.collections
        },
        {
          indexName: CONFIG.algolia.indices.pages,
          params: 'query=' + q + '&hitsPerPage=' + CONFIG.limits.pages
        }
      ]
    };

    fetch(buildAlgoliaUrl(), {
      method: 'POST',
      headers: {
        'X-Algolia-Application-Id': CONFIG.algolia.appId,
        'X-Algolia-API-Key': CONFIG.algolia.searchApiKey,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: JSON.stringify(body),
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Algolia HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (_abortController === controller) _abortController = null;
        if (controller && controller.signal.aborted) return;

        var results = (data && data.results) || [];
        var productsRaw    = (results[0] && results[0].hits) || [];
        var collectionsRaw = (results[1] && results[1].hits) || [];
        var pagesRaw       = (results[2] && results[2].hits) || [];

        // Drop pages whose Shopify ID is in the hide list (Algolia's objectID
        // for shopify_pages records is the bare numeric Shopify page ID).
        if (CONFIG.hidePageIds && CONFIG.hidePageIds.length) {
          pagesRaw = pagesRaw.filter(function (p) {
            return CONFIG.hidePageIds.indexOf(String(p.objectID)) === -1;
          });
        }

        // Re-shape into what renderFlydown expects.
        var shaped = {
          resources: {
            results: {
              products:    productsRaw.map(mapAlgoliaProduct),
              collections: collectionsRaw.map(mapAlgoliaCollection),
              pages:       pagesRaw.map(mapAlgoliaPage)
            }
          }
        };
        callback(null, shaped);
      })
      .catch(function (err) {
        if (_abortController === controller) _abortController = null;
        if (err.name !== 'AbortError') {
          callback(err, null);
        }
      });
  }

  // ════════════════════════════════════════════
  // Price formatting
  // ════════════════════════════════════════════
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents.replace('.', ''), 10);
    var dollars = (cents / 100).toFixed(2);
    if (dollars.indexOf('.00') === dollars.length - 3) {
      dollars = dollars.slice(0, -3);
    }
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ════════════════════════════════════════════
  // Render flydown HTML
  // ════════════════════════════════════════════
  // ════════════════════════════════════════════
  // Result filtering helpers
  // ════════════════════════════════════════════
  function containsAny(str, terms) {
    if (!str || !terms || !terms.length) return false;
    var lower = str.toLowerCase();
    for (var i = 0; i < terms.length; i++) {
      if (lower.indexOf(terms[i].toLowerCase()) !== -1) return true;
    }
    return false;
  }

  function filterAndSortProducts(products) {
    // 1. Hide products whose title matches hidden terms
    var visible = products.filter(function (p) {
      return !containsAny(p.title || '', CONFIG.hideProductTitleContains);
    });

    // 2. Sort: deprioritized products go to the bottom
    // The API returns product type as "type" (not "product_type")
    var normal = [];
    var deprioritized = [];
    for (var i = 0; i < visible.length; i++) {
      var p = visible[i];
      var isDeprioritized =
        containsAny(p.type || '', CONFIG.deprioritizeProductTypes) ||
        containsAny(p.title || '', CONFIG.deprioritizeProductTitleContains);
      if (isDeprioritized) {
        deprioritized.push(p);
      } else {
        normal.push(p);
      }
    }
    visible = normal.concat(deprioritized);

    return visible;
  }

  function filterPages(pages) {
    if (!CONFIG.hidePageUrlContains.length) return pages;
    return pages.filter(function (p) {
      return !containsAny(p.url || '', CONFIG.hidePageUrlContains);
    });
  }

  function filterCollections(collections) {
    if (!CONFIG.hideCollectionUrlContains.length) return collections;
    return collections.filter(function (c) {
      return !containsAny(c.url || '', CONFIG.hideCollectionUrlContains);
    });
  }

  function renderFlydown(data, query) {
    var resources = data.resources || {};
    var results = resources.results || {};

    // Apply filtering rules before slicing to limits
    var allProducts = filterAndSortProducts(results.products || []);
    var allCollections = filterCollections(results.collections || []);
    var allPages = filterPages(results.pages || []);

    var products = allProducts.slice(0, CONFIG.limits.display);
    var collections = allCollections.slice(0, CONFIG.limits.collections);
    var pages = allPages.slice(0, CONFIG.limits.pages);

    var hasLeft = collections.length > 0 || pages.length > 0;
    var hasRight = products.length > 0;

    if (!hasLeft && !hasRight) {
      return '<div class="ps-flydown__no-results">No results found for &ldquo;' + escapeHtml(query) + '&rdquo;</div>';
    }

    var html = '<div class="ps-flydown">';

    // ── Left column: Collections + Pages ──
    if (hasLeft) {
      html += '<div class="ps-flydown__left">';

      if (collections.length > 0) {
        html += '<div class="ps-flydown__section-title">Collections</div>';
        html += '<ul class="ps-flydown__link-list">';
        for (var c = 0; c < collections.length; c++) {
          html += '<li class="ps-flydown__link-item"><a href="' + escapeHtml(collections[c].url) + '">' + escapeHtml(collections[c].title) + '</a></li>';
        }
        html += '</ul>';
      }

      if (pages.length > 0) {
        html += '<div class="ps-flydown__section-title">Pages</div>';
        html += '<ul class="ps-flydown__link-list">';
        for (var p = 0; p < pages.length; p++) {
          html += '<li class="ps-flydown__link-item"><a href="' + escapeHtml(pages[p].url) + '">' + escapeHtml(pages[p].title) + '</a></li>';
        }
        html += '</ul>';
      }

      html += '</div>';
    }

    // ── Right column: Products ──
    if (hasRight) {
      html += '<div class="ps-flydown__right">';
      html += '<div class="ps-flydown__section-title">Products</div>';
      html += '<div class="ps-flydown__products">';

      for (var i = 0; i < products.length; i++) {
        var prod = products[i];

        // Resolve image URL from API response (format varies)
        var imgSrc = '';
        if (typeof prod.featured_image === 'string' && prod.featured_image) {
          imgSrc = prod.featured_image;
        } else if (prod.featured_image && prod.featured_image.url) {
          imgSrc = prod.featured_image.url;
        } else if (prod.image) {
          imgSrc = prod.image;
        }
        // Rewrite CDN URL from cdn.shopify.com → topture.com
        imgSrc = rewriteImageUrl(imgSrc);

        var price = '';
        var comparePrice = '';
        // Null-guard: Algolia hits can lack price data — formatMoney(null)
        // renders "$0", which reads as a real (wrong) price.
        if (prod.variants && prod.variants.length > 0 && prod.variants[0].price != null) {
          var variant = prod.variants[0];
          price = formatMoney(variant.price);
          if (variant.compare_at_price && parseInt(variant.compare_at_price) > parseInt(variant.price)) {
            comparePrice = formatMoney(variant.compare_at_price);
          }
        } else if (prod.price != null && prod.price !== '') {
          price = formatMoney(prod.price);
        }

        html += '<a class="ps-flydown__product" href="' + escapeHtml(prod.url) + '">';
        if (imgSrc) {
          html += '<div class="ps-flydown__product-image"><img src="' + imgSrc + '" alt="' + escapeHtml(prod.title) + '" loading="eager"></div>';
        }
        html += '<div class="ps-flydown__product-info">';
        html += '<div class="ps-flydown__product-title">' + escapeHtml(prod.title) + '</div>';
        html += '<div class="ps-flydown__product-price">';
        if (comparePrice) {
          html += '<span class="ps-flydown__product-price--compare">' + comparePrice + '</span>';
        }
        html += price;
        html += '</div>';
        html += '</div>';
        html += '</a>';
      }

      html += '</div>'; // .ps-flydown__products

      // "View all" link
      html += '<div class="ps-flydown__view-all">';
      html += '<a href="/search?q=' + encodeURIComponent(query) + '">';
      html += 'View all products <span class="ps-flydown__view-all-arrow">&rarr;</span>';
      html += '</a>';
      html += '</div>';

      html += '</div>'; // .ps-flydown__right
    }

    html += '</div>'; // .ps-flydown
    return html;
  }

  // ════════════════════════════════════════════
  // HTML escape
  // ════════════════════════════════════════════
  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ════════════════════════════════════════════
  // Debounce
  // ════════════════════════════════════════════
  function debounce(fn, ms) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  // ════════════════════════════════════════════
  // Loading state
  // ════════════════════════════════════════════
  function showLoading(container) {
    container.innerHTML = '<div class="ps-flydown__loading"><div class="ps-flydown__spinner"></div></div>';
  }

  // ════════════════════════════════════════════
  // Main init
  // ════════════════════════════════════════════
  function init() {
    // ── Grab DOM refs ──
    var input = document.querySelector('[data-live-search-input]');
    var flydown = document.querySelector('[data-live-search-flydown]');
    var liveSearchRoot = document.querySelector('[data-live-search]');
    var searchForm = document.querySelector('[data-live-search-form]');
    var quicklinks = flydown ? flydown.querySelector('.search-flydown--quicklinks') : null;

    if (!input || !flydown) return;

    injectStyles();

    // Quick links heading: "Popular searches" is rendered by live-search-form.liquid inside
    // a <template> that is only copied in on first focus, so it can't be restyled here at
    // init — its colour lives in injectStyles().

    // ── Create our own results container inside the flydown ──
    var psContainer = document.createElement('div');
    psContainer.id = 'ps-custom-results';
    flydown.appendChild(psContainer);

    // ══════════════════════════════════════════
    // Screen-reader status region — WCAG 4.1.3
    // ──────────────────────────────────────────
    // Measured on live 2026-07-31: typing "sauna" rendered 16 results and the
    // flydown contained ZERO live regions, so a screen-reader user heard
    // nothing at all — the results existed only visually. This region is the
    // fix. It lives OUTSIDE psContainer because psContainer.innerHTML is
    // replaced on every keystroke; a live region that gets destroyed and
    // recreated does not reliably announce in NVDA or VoiceOver.
    // ══════════════════════════════════════════
    var srStatus = document.createElement('div');
    srStatus.className = 'ps-sr-status';
    srStatus.setAttribute('role', 'status');
    srStatus.setAttribute('aria-live', 'polite');
    srStatus.setAttribute('aria-atomic', 'true');
    flydown.appendChild(srStatus);

    var lastAnnouncement = '';
    function announce(msg) {
      if (msg === lastAnnouncement) {
        // Identical text written twice is not re-announced by most screen
        // readers. Blank it first so a repeat result count is still spoken.
        srStatus.textContent = '';
      }
      lastAnnouncement = msg;
      srStatus.textContent = msg;
    }

    // Count what actually rendered and say it. Called after every innerHTML
    // write so the announcement can never drift from what is on screen.
    function announceResults(query) {
      var products = psContainer.querySelectorAll('.ps-flydown__product').length;
      var links = psContainer.querySelectorAll('.ps-flydown__link-item a').length;
      var total = products + links;
      if (psContainer.querySelector('.ps-flydown__no-results')) {
        announce('No results for ' + query + '.');
        return;
      }
      announce(total + (total === 1 ? ' result' : ' results') + ' for ' + query
        + '. Use the down arrow key to review them.');
    }

    // ── State ──
    var lastQuery = '';
    var isSearching = false;
    // Guards the input's focus handler while we deliberately move focus back to
    // the input as part of CLOSING the mobile overlay. Without it, restoring
    // focus re-triggers openMobileOverlay(). See closeMobileOverlay().
    var suppressReopen = false;

    // ══════════════════════════════════════════
    // Empire animation integration
    // ──────────────────────────────────────────
    // Empire's CSS relies on:
    //   - .live-search--focused on liveSearchRoot → shows flydown
    //   - .live-search--takeover on liveSearchRoot → mobile full-screen
    //   - data-animation-state="open" on liveSearchRoot → triggers transitions
    //   - data-animation-state="visible" on quicklinks → opacity: 1
    //   - --open-height CSS var on flydown → controls height
    //
    // We cooperate by:
    //   - Letting Empire's event handlers run (no stopImmediatePropagation)
    //   - Setting the animation attributes Empire's CSS expects
    //   - Using our own container + .ps-searching to hide Empire's results
    //   - On desktop only: overriding height (Empire collapses to 1px)
    //   - On mobile: setting takeover class + display:block on flydown
    // ══════════════════════════════════════════

    // ══════════════════════════════════════════
    // iOS-proof scroll lock for the mobile search takeover
    // ══════════════════════════════════════════
    // `body { overflow: hidden }` alone does NOT stop touch-scroll on iOS
    // Safari (same gotcha that hit the bundle modal). Pin the body via
    // position:fixed + top: -scrollY, and on unlock restore scroll with
    // scroll-behavior: auto to defeat the theme's `html { scroll-behavior:
    // smooth }` which would otherwise animate the restore visibly.
    //
    // _preSavedScrollY: focusing a mobile input causes the browser to
    // auto-scroll the input into view, so by the time lockScroll() runs
    // from the focus handler, window.scrollY has already snapped to 0.
    // Capture scroll on touchstart / mousedown BEFORE focus to get the
    // real pre-tap position.
    var _scrollLockY = 0;
    var _scrollLockCount = 0;
    var _preSavedScrollY = null;
    function preSaveScrollY() {
      _preSavedScrollY = window.scrollY || window.pageYOffset || 0;
    }
    function lockScroll() {
      if (_scrollLockCount === 0) {
        _scrollLockY = _preSavedScrollY != null
          ? _preSavedScrollY
          : (window.scrollY || window.pageYOffset || 0);
        _preSavedScrollY = null;
        document.body.style.position = 'fixed';
        document.body.style.top = -_scrollLockY + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      }
      _scrollLockCount++;
    }
    function unlockScroll() {
      _scrollLockCount = Math.max(0, _scrollLockCount - 1);
      if (_scrollLockCount === 0) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        var html = document.documentElement;
        var prevBehavior = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, _scrollLockY);
        html.style.scrollBehavior = prevBehavior;
      }
    }

    // Single-shot flag for the per-session scroll lock.
    // setEmpireAnimationOpen is called from multiple paths (focus,
    // enterSearchMode, exitSearchMode-with-focus, doSearch fallback), and
    // before this flag each call invoked lockScroll() unconditionally,
    // incrementing _scrollLockCount past 1. clearEmpireAnimation only
    // unlocks once per close, so after typing-then-closing the count
    // stayed at 1 and the page remained scroll-locked.
    var _empireLockedScroll = false;

    function setEmpireAnimationOpen() {
      // Set data-animation-state on liveSearchRoot (NOT flydown)
      // The theme's CSS targets: .live-search--takeover[data-animation-state="open"] .search-flydown
      // No guard — set unconditionally (flydown may not have data-animation initially)
      if (liveSearchRoot) {
        liveSearchRoot.setAttribute('data-animation-state', 'open');
      }

      // Make quicklinks visible (Empire uses opacity: 0 → 1 via this attr)
      if (quicklinks) {
        quicklinks.setAttribute('data-animation-state', 'visible');
      }

      // On mobile: full-screen takeover
      if (isMobile() && liveSearchRoot) {
        liveSearchRoot.classList.add('live-search--takeover');
        // iOS-proof scroll lock — replaces the previous body.overflow=hidden
        // which iOS Safari ignores for touch-scroll. Single-shot per session.
        if (!_empireLockedScroll) {
          lockScroll();
          _empireLockedScroll = true;
        }
      }
    }

    function clearEmpireAnimation() {
      // Reverse everything setEmpireAnimationOpen() did
      if (liveSearchRoot) {
        liveSearchRoot.removeAttribute('data-animation-state');
        liveSearchRoot.classList.remove('live-search--takeover');
      }
      flydown.style.removeProperty('display');
      flydown.style.removeProperty('overflow');
      if (_empireLockedScroll) {
        unlockScroll();
        _empireLockedScroll = false;
      }
    }

    function forceFlydownOpenDesktop() {
      // Desktop only: position flydown below search bar, matching its width
      if (!isMobile()) {
        flydown.style.setProperty('position', 'absolute', 'important');
        flydown.style.setProperty('top', '100%', 'important');
        flydown.style.setProperty('left', '0', 'important');
        flydown.style.setProperty('right', '0', 'important');
        flydown.style.setProperty('width', '100%', 'important');
        flydown.style.setProperty('height', 'auto', 'important');
        flydown.style.setProperty('max-height', '70vh', 'important');
        flydown.style.setProperty('overflow', 'visible', 'important');
        flydown.style.setProperty('display', 'block', 'important');
      }
    }

    function resetFlydownStyles() {
      flydown.style.removeProperty('position');
      flydown.style.removeProperty('left');
      flydown.style.removeProperty('right');
      flydown.style.removeProperty('width');
      flydown.style.removeProperty('top');
      flydown.style.removeProperty('height');
      flydown.style.removeProperty('max-height');
      flydown.style.removeProperty('overflow');
      flydown.style.removeProperty('--open-height');
      flydown.style.removeProperty('display');
    }

    // ── Mobile header management ──
    var mobileHeader = null;
    var mobileInput = null;

    function createMobileHeader() {
      if (mobileHeader) return; // already exists
      mobileHeader = document.createElement('div');
      mobileHeader.className = 'ps-mobile-header';
      // The input carries an explicit aria-label: a placeholder is not an
      // accessible name substitute because it disappears the moment the user
      // types, leaving the field unnamed exactly when it holds their query
      // (WCAG 3.3.2). type="search" also gets mobile keyboards the search key.
      mobileHeader.innerHTML =
        '<button type="button" class="ps-mobile-header__back" aria-label="Close search">&larr;</button>' +
        '<input class="ps-mobile-header__input" type="search" aria-label="Search products"'
          + ' placeholder="Search..." autocomplete="off" />' +
        '<button type="button" class="ps-mobile-header__clear" aria-label="Clear search">&times;</button>';

      // Insert at top of flydown
      flydown.insertBefore(mobileHeader, flydown.firstChild);

      mobileInput = mobileHeader.querySelector('.ps-mobile-header__input');

      // Sync value from original input
      mobileInput.value = input.value;

      // Back button closes search
      mobileHeader.querySelector('.ps-mobile-header__back').addEventListener('click', function () {
        closeFlydown();
        input.blur();
      });

      // Clear button — clear results but stay in overlay
      mobileHeader.querySelector('.ps-mobile-header__clear').addEventListener('click', function () {
        mobileInput.value = '';
        input.value = '';
        isSearching = false;
        lastQuery = '';
        flydown.classList.remove('ps-searching');
        psContainer.classList.remove('ps-visible');
        psContainer.innerHTML = '';
        announce('Search cleared.');
        mobileInput.focus();
      });

      // Sync mobile input → original input + trigger search
      mobileInput.addEventListener('input', function () {
        input.value = mobileInput.value;
        var query = mobileInput.value.trim();
        if (query.length < CONFIG.minChars) {
          exitSearchMode();
          lastQuery = '';
          return;
        }
        doSearch();
      });

      // Focus the mobile input
      setTimeout(function () { mobileInput.focus(); }, 50);
    }

    function removeMobileHeader() {
      if (mobileHeader && mobileHeader.parentNode) {
        mobileHeader.parentNode.removeChild(mobileHeader);
      }
      mobileHeader = null;
      mobileInput = null;
    }

    // ── Mobile overlay (independent of search threshold) ──
    var mobileOverlayOpen = false;

    // Focus-trap handle for the mobile overlay (see snippets/a11y-dialog.liquid).
    var releaseOverlayFocus = null;

    function openMobileOverlay() {
      if (!isMobile() || mobileOverlayOpen) return;
      mobileOverlayOpen = true;
      flydown.classList.add('ps-active');
      flydown.classList.add('ps-mobile-open');
      document.body.classList.add('ps-overlay-active');
      setEmpireAnimationOpen();
      createMobileHeader();

      // ── Modal semantics, mobile only (WCAG 4.1.2 / 2.4.3) ──
      // On mobile this flydown is a full-screen takeover that covers the entire
      // page, but it declared no role at all: measured on live 2026-07-31, the
      // flydown had role=null, aria-modal=null, and #site-main was neither
      // [inert] nor aria-hidden — so the page underneath stayed in the
      // accessibility tree while being visually unreachable.
      //
      // Desktop deliberately gets NONE of this: there the flydown is a
      // non-modal suggestion list sitting under the search bar, and calling it a
      // dialog would be a lie that costs keyboard users their normal Tab flow.
      flydown.setAttribute('role', 'dialog');
      flydown.setAttribute('aria-modal', 'true');
      flydown.setAttribute('aria-label', 'Search');

      if (window.ToptureA11yDialog) {
        releaseOverlayFocus = window.ToptureA11yDialog.trap(flydown, {
          initialFocus: mobileInput,
          returnFocusTo: input
        });
      }
    }

    function closeMobileOverlay() {
      mobileOverlayOpen = false;
      flydown.classList.remove('ps-mobile-open');
      document.body.classList.remove('ps-overlay-active');

      flydown.removeAttribute('role');
      flydown.removeAttribute('aria-modal');
      flydown.removeAttribute('aria-label');

      // Releasing returns focus to the header search input (returnFocusTo).
      // suppressReopen is load-bearing: the input's own focus handler calls
      // openMobileOverlay(), so restoring focus would otherwise re-open the
      // overlay we are in the middle of closing — an infinite bounce.
      //
      // Touch callers that want no focus at all (the back button) simply
      // blur() afterwards; that runs after this restore and wins.
      if (releaseOverlayFocus) {
        var release = releaseOverlayFocus;
        releaseOverlayFocus = null;
        suppressReopen = true;
        release();
        suppressReopen = false;
      }
    }

    function enterSearchMode() {
      if (isSearching) return;
      isSearching = true;
      flydown.classList.add('ps-active');
      flydown.classList.add('ps-searching');
      psContainer.classList.add('ps-visible');
      setEmpireAnimationOpen();
      forceFlydownOpenDesktop();

      // On mobile, ensure overlay is open (may already be from focus)
      if (isMobile()) {
        openMobileOverlay();
      }
    }

    function exitSearchMode() {
      // Exit search mode — hide results but keep overlay open on mobile
      isSearching = false;
      flydown.classList.remove('ps-searching');
      psContainer.classList.remove('ps-visible');
      psContainer.innerHTML = '';

      if (isMobile() && mobileOverlayOpen) {
        // On mobile: stay in full-screen overlay, just clear results
        // The overlay stays open until user taps back button
        return;
      }

      var inputFocused = document.activeElement === input;

      if (inputFocused) {
        // Desktop: input still focused — keep flydown open for quick links
        // Do NOT force full-width — quick links should stay within search bar width
        setEmpireAnimationOpen();
        resetFlydownStyles();
      } else {
        flydown.classList.remove('ps-active');
        clearEmpireAnimation();
        resetFlydownStyles();
      }
    }

    function closeFlydown() {
      isSearching = false;
      lastQuery = '';
      flydown.classList.remove('ps-active');
      flydown.classList.remove('ps-searching');
      psContainer.classList.remove('ps-visible');
      psContainer.innerHTML = '';
      closeMobileOverlay();
      removeMobileHeader();
      clearEmpireAnimation();
      resetFlydownStyles();
    }

    // ── Debounced search ──
    var doSearch = debounce(function () {
      var query = input.value.trim();

      if (query.length < CONFIG.minChars) {
        exitSearchMode();
        lastQuery = '';
        return;
      }

      if (query === lastQuery) return;
      lastQuery = query;

      enterSearchMode();
      showLoading(psContainer);

      fetchPredictiveSearch(query, function (err, data) {
        if (err) {
          psContainer.innerHTML = '<div class="ps-flydown__no-results">Something went wrong. Please try again.</div>';
          announce('Search failed. Please try again.');
          // Clear so retyping the SAME query re-issues the request —
          // otherwise the `query === lastQuery` guard suppresses every retry.
          lastQuery = '';
          return;
        }
        psContainer.innerHTML = renderFlydown(data, query);
        announceResults(query);
      });
    }, CONFIG.debounceMs);

    // ── Direct (non-debounced) search for refocus ──
    function doSearchImmediate(query) {
      lastQuery = query;
      enterSearchMode();
      showLoading(psContainer);

      fetchPredictiveSearch(query, function (err, data) {
        if (err) {
          psContainer.innerHTML = '<div class="ps-flydown__no-results">Something went wrong. Please try again.</div>';
          announce('Search failed. Please try again.');
          lastQuery = ''; // allow retry of the same query
          return;
        }
        psContainer.innerHTML = renderFlydown(data, query);
        announceResults(query);
      });
    }

    // ── Bind input event ──
    // We listen in capture phase but do NOT block Empire's handlers.
    // Empire's native search will fire too, but our CSS hides its results
    // via .ps-searching .search-flydown--results { display: none }
    input.addEventListener('input', function () {
      var query = input.value.trim();

      if (query.length < CONFIG.minChars) {
        exitSearchMode();
        lastQuery = '';
        return;
      }

      doSearch();
    }, true);

    // Capture scroll position on touchstart/mousedown BEFORE focus
    // auto-scrolls the input into view (which would zero out scrollY).
    input.addEventListener('touchstart', function () {
      if (isMobile()) preSaveScrollY();
    }, { passive: true });
    input.addEventListener('mousedown', function () {
      if (isMobile()) preSaveScrollY();
    });

    // ── Focus: open mobile overlay immediately, or desktop flydown ──
    input.addEventListener('focus', function () {
      // Set while closeMobileOverlay() restores focus to this input. Without
      // the guard the restore immediately re-opens the overlay.
      if (suppressReopen) return;

      if (isMobile()) {
        // Mobile: immediately open full-screen overlay (before any typing)
        openMobileOverlay();
      } else {
        // Desktop: set animation attrs so Empire shows quick links
        // Do NOT force full-width — quick links should stay within search bar width
        setEmpireAnimationOpen();
      }

      var query = input.value.trim();
      if (query.length >= CONFIG.minChars) {
        // Re-fetch on refocus so custom results take priority
        doSearchImmediate(query);
      }
    });

    // ── Close on click outside ──
    document.addEventListener('click', function (e) {
      if (liveSearchRoot && !liveSearchRoot.contains(e.target)) {
        closeFlydown();
      }
    });

    // ══════════════════════════════════════════
    // Keyboard: Escape to dismiss, arrows to review results
    // ──────────────────────────────────────────
    // Previously this handler was Escape-only and ended in input.blur(), which
    // measured as focus landing on <body> — the keyboard user lost their place
    // entirely (WCAG 2.4.3). It also ran on EVERY Escape anywhere on the page,
    // including while the flydown was already shut.
    // ══════════════════════════════════════════

    /** Every result link, in visual order, that a user can actually reach. */
    function resultLinks() {
      if (!psContainer.classList.contains('ps-visible')) return [];
      return [].slice.call(psContainer.querySelectorAll('a[href]')).filter(function (a) {
        return a.offsetParent !== null;
      });
    }

    function activeSearchField() {
      return (mobileInput && document.activeElement === mobileInput) ? mobileInput : input;
    }

    document.addEventListener('keydown', function (e) {
      var isOpen = mobileOverlayOpen || flydown.classList.contains('ps-active');

      if (e.key === 'Escape' || e.keyCode === 27) {
        if (!isOpen) return;          // don't swallow Escape for other components
        e.preventDefault();
        closeFlydown();
        // Return focus to the search field rather than blurring to <body>.
        // closeMobileOverlay() already put it there on mobile (with reopen
        // suppressed); this covers desktop, where nothing had moved it.
        if (!isMobile()) input.focus();
        return;
      }

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!isOpen) return;

      var links = resultLinks();
      if (!links.length) return;

      var field = activeSearchField();
      var active = document.activeElement;
      var idx = links.indexOf(active);

      if (active === field) {
        // From the text field: Down enters the list, Up jumps to the end.
        // Don't steal Left/Right — those are still caret movement.
        e.preventDefault();
        links[e.key === 'ArrowDown' ? 0 : links.length - 1].focus();
        return;
      }
      if (idx === -1) return;         // focus is somewhere else entirely

      e.preventDefault();
      if (e.key === 'ArrowDown') {
        if (idx === links.length - 1) field.focus();   // wrap back to the input
        else links[idx + 1].focus();
      } else {
        if (idx === 0) field.focus();
        else links[idx - 1].focus();
      }
    });

    // ── Close on form submit ──
    if (searchForm) {
      searchForm.addEventListener('submit', function () {
        closeFlydown();
      });
    }
  }

  // ════════════════════════════════════════════
  // Boot — Predictive Search Flydown
  // ════════════════════════════════════════════
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ════════════════════════════════════════════════════════════════
  // SEARCH RESULTS PAGE — Filter & Re-order Product Cards
  // ════════════════════════════════════════════════════════════════
  // Runs on /search?q=… pages. Uses the same CONFIG rules defined
  // above to hide and deprioritize products on the full results grid.
  //
  // Strategy:
  //   1. Title-based: instantly hide/deprioritize using title text
  //   2. Product-type-based: fetch each product's .json in background
  //      batches, then apply type-based deprioritization once loaded.
  // ════════════════════════════════════════════════════════════════

  function markSorted(grid) {
    if (grid && !grid.hasAttribute('data-ps-sorted')) {
      grid.setAttribute('data-ps-sorted', 'true');
    }
  }

  function initSearchResultsFilter() {
    // Only run on the search results page
    if (window.location.pathname !== '/search') return;

    var grid = document.querySelector('.productgrid--items');
    if (!grid) return;

    // Failsafe: no matter what happens below, reveal the grid within 800 ms.
    // The grid is hidden via CSS until data-ps-sorted is set; we don't want
    // a JS error or slow code path to leave it invisible forever.
    setTimeout(function () { markSorted(grid); }, 800);

    var cards = grid.querySelectorAll('[data-product-item]');
    if (!cards.length) { markSorted(grid); return; }

    // ── Helpers ──

    function getCardTitle(card) {
      var el = card.querySelector('.productitem--title');
      return el ? el.textContent.trim() : '';
    }

    function getCardHandle(card) {
      var link = card.querySelector('.productitem--title a') ||
                 card.querySelector('.productitem--image-link');
      if (!link) return '';
      var href = link.getAttribute('href') || '';
      // Extract handle from /products/some-handle or /products/some-handle?variant=…
      var match = href.match(/\/products\/([^?#/]+)/);
      return match ? match[1] : '';
    }

    // ── Phase 1: Title-based filtering (instant) ──

    var visibleCards = [];

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var title = getCardTitle(card);

      // Hide cards whose title matches hidden terms
      if (containsAny(title, CONFIG.hideProductTitleContains)) {
        card.style.display = 'none';
        card.setAttribute('data-ps-hidden', 'true');
        continue;
      }

      visibleCards.push({
        el: card,
        title: title,
        handle: getCardHandle(card),
        productType: null,         // filled in Phase 2
        deprioritized: false
      });
    }

    // Mark title-based deprioritized cards
    for (var j = 0; j < visibleCards.length; j++) {
      var vc = visibleCards[j];
      if (containsAny(vc.title, CONFIG.deprioritizeProductTitleContains)) {
        vc.deprioritized = true;
      }
    }

    // Re-order: move deprioritized cards to the bottom of the grid
    reorderGrid(visibleCards, grid);

    // Phase 1 reorder is done — reveal the grid now. If Phase 2 below runs
    // and does its own reorder, the grid stays visible and reorders in place.
    markSorted(grid);

    // ── Phase 2: Product-type-based filtering ──
    // Prefer the data-product-type attribute rendered by Liquid in
    // snippets/product-grid-item.liquid — zero network requests. Fall back
    // to fetching /products/HANDLE.json only for cards that don't expose
    // the attribute (older templates or non-standard grid contexts).

    if (CONFIG.deprioritizeProductTypes.length) {
      var cardsNeedingFetch = [];
      for (var m = 0; m < visibleCards.length; m++) {
        var vcard = visibleCards[m];
        var inlineType = vcard.el.getAttribute('data-product-type');
        if (inlineType !== null) {
          vcard.productType = inlineType;
        } else {
          cardsNeedingFetch.push(vcard);
        }
      }

      var applyAndReorder = function () {
        for (var k = 0; k < visibleCards.length; k++) {
          var item = visibleCards[k];
          if (!item.deprioritized && item.productType) {
            if (containsAny(item.productType, CONFIG.deprioritizeProductTypes)) {
              item.deprioritized = true;
            }
          }
        }
        reorderGrid(visibleCards, grid);
      };

      if (cardsNeedingFetch.length === 0) {
        applyAndReorder();
      } else {
        fetchProductTypes(cardsNeedingFetch, applyAndReorder);
      }
    }
  }

  function reorderGrid(visibleCards, grid) {
    var normal = [];
    var deprioritized = [];

    for (var i = 0; i < visibleCards.length; i++) {
      if (visibleCards[i].deprioritized) {
        deprioritized.push(visibleCards[i].el);
      } else {
        normal.push(visibleCards[i].el);
      }
    }

    // Append in order: normal first, then deprioritized
    // This naturally moves them without removing hidden cards
    for (var n = 0; n < normal.length; n++) {
      grid.appendChild(normal[n]);
    }
    for (var d = 0; d < deprioritized.length; d++) {
      grid.appendChild(deprioritized[d]);
    }
  }

  function fetchProductTypes(visibleCards, callback) {
    // Fetch product type for each card via /products/HANDLE.json
    // Batch in groups of 4 to avoid hammering the server
    var batchSize = 4;
    var idx = 0;

    function nextBatch() {
      if (idx >= visibleCards.length) {
        callback();
        return;
      }

      var batch = [];
      var end = Math.min(idx + batchSize, visibleCards.length);

      for (var b = idx; b < end; b++) {
        (function (item) {
          if (!item.handle || item.productType) {
            batch.push(Promise.resolve());
            return;
          }
          var p = fetch('/products/' + item.handle + '.json')
            .then(function (r) { return r.json(); })
            .then(function (data) {
              if (data && data.product) {
                item.productType = data.product.product_type || '';
              }
            })
            .catch(function () { /* silently skip */ });
          batch.push(p);
        })(visibleCards[b]);
      }

      idx = end;
      Promise.all(batch).then(nextBatch);
    }

    nextBatch();
  }

  // Boot — Search Results Filter
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchResultsFilter);
  } else {
    initSearchResultsFilter();
  }
})();
